import type { PrintLayout, PrintOutcome, PrintRecordDto } from "@delta/shared";
import { api, ApiFailure } from "../lib/api";
import { agentCall, canDirectPrint, savedToken, type AgentReply } from "../lib/agent";
import { loadPrintFonts, renderDoc, type PrintDoc, type Raster } from "./render";

// Direct printing from the screen (phase 12a; docs/direct-printing.md): claim the print with Delta (the original
// once, or the next copy), render the pages, hand them to the POS agent on this PC, follow the job and tell Delta what
// happened. A print never repeats a sale, a payment, a stock movement or a posting: it only adds print records.
//
// A print whose claim reached Delta but whose job may not have reached the agent is kept in this browser
// (localStorage, "delta.print.pending") with its job id, so the screen can look it up again instead of guessing.

export type Role = "receipt" | "a4" | "label";
export interface AgentPaper { kind: "58mm" | "80mm" | "a4" | "label"; widthMm: number; heightMm: number | null; dpi: number }
export interface AgentMapping { role: Role; printer: string; mode: "escpos" | "driver"; paper: AgentPaper; cutter: boolean; drawer: boolean; copies?: number; dots: number; updatedAt: string; updatedBy: string }
export interface AgentPrinter { name: string; driver: string; port: string; connection: string; isDefault: boolean; status: { state: string; problems: string[] } }
export interface AgentPrinters { backend: "windows" | "fake" | "none"; printers: AgentPrinter[]; roles: AgentMapping[]; discoveryProblem: string | null; jobs: Record<string, number> }
export type AgentJobState = "queued" | "sending" | "spooled" | "done" | "failed" | "unknown" | "cancelled";
export interface AgentJob { jobId: string; role: Role; documentRef: string; copy: boolean; copies?: number; state: AgentJobState; attempts: number; error: string | null; detail: string | null; printer: string | null; confirmedBy: string | null; createdAt: string }

const WS_KEY = "delta.workstation", PENDING_KEY = "delta.print.pending";
const read = (k: string) => { try { return localStorage.getItem(k); } catch { return null; } };
const write = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* storage blocked: nothing kept */ } };

/** This workstation's name as people know it ("Till 1"), set on the printers page; the agent knows it by pairing. */
export const workstationName = () => read(WS_KEY) || "This PC";
export const setWorkstationName = (n: string) => write(WS_KEY, n.trim().slice(0, 80));

export const agentPrinters = () => agentCall<AgentPrinters>("GET", "/printers");
export const agentJob = (jobId: string) => agentCall<AgentJob>("GET", `/print/jobs/${jobId}`);
export const agentJobsFor = (documentRef: string) => agentCall<AgentJob[]>("GET", `/print/jobs?documentRef=${encodeURIComponent(documentRef)}`);

/** The mapping of a role on this PC, or why there is none. */
export async function roleMapping(role: Role): Promise<{ ok: true; mapping: AgentMapping } | { ok: false; reason: "no_agent" | "not_paired" | "no_printer" }> {
  if (!canDirectPrint()) return { ok: false, reason: "not_paired" };
  const r = await agentPrinters();
  if (r.status === null) return { ok: false, reason: "no_agent" };
  if (r.status === 401 || r.status === 403) return { ok: false, reason: "not_paired" };
  const m = r.body?.roles.find((x) => x.role === role);
  return m ? { ok: true, mapping: m } : { ok: false, reason: "no_printer" };
}

interface Pending { jobId: string; invoiceId: string; recordId: string; documentRef: string }
function pending(): Pending[] { try { return JSON.parse(read(PENDING_KEY) ?? "[]"); } catch { return []; } }
function keep(p: Pending) { write(PENDING_KEY, JSON.stringify([...pending().filter((x) => x.jobId !== p.jobId), p])); }
function drop(jobId: string) { write(PENDING_KEY, JSON.stringify(pending().filter((x) => x.jobId !== jobId))); }
export const pendingFor = (invoiceId: string) => pending().filter((p) => p.invoiceId === invoiceId);

export const documentRef = (invoiceId: string, copyNo: number) => `invoice:${invoiceId}:${copyNo === 0 ? "original" : `copy:${copyNo}`}`;

/** What the screen shows about one print. */
export type PrintView =
  | { kind: "working"; state: "claiming" | "rendering" | "sending" | AgentJobState; record?: PrintRecordDto; job?: AgentJob }
  | { kind: "done"; record: PrintRecordDto; job: AgentJob }
  | { kind: "failed"; record?: PrintRecordDto; job?: AgentJob; reason: string }
  | { kind: "unknown"; record: PrintRecordDto; job?: AgentJob; reason: string }
  | { kind: "original_exists"; record: PrintRecordDto };

const TERMINAL: AgentJobState[] = ["done", "failed", "unknown", "cancelled"];

async function report(recordId: string, outcome: Exclude<PrintOutcome, "requested">, detail?: string | null) {
  try { return await api<PrintRecordDto>("POST", `/prints/${recordId}/outcome`, { outcome, ...(detail ? { detail: detail.slice(0, 300) } : {}) }); } catch { return null; }
}

/**
 * Follows a job on the agent until it ends (or `maxMs`), telling Delta each new state. Resolves with the last view.
 */
export async function follow(record: PrintRecordDto, jobId: string, onView: (v: PrintView) => void, maxMs = 120_000): Promise<PrintView> {
  const end = Date.now() + maxMs;
  let last: AgentJobState | null = null, misses = 0;
  for (;;) {
    const r = await agentJob(jobId);
    if (r.body) {
      misses = 0;
      const j = r.body;
      if (j.state !== last && j.state !== "sending") { const rec = await report(record.id, j.state, j.error); if (rec) record = rec; }
      last = j.state;
      if (j.state === "done") { drop(jobId); const v: PrintView = { kind: "done", record, job: j }; onView(v); return v; }
      if (j.state === "unknown") { drop(jobId); const v: PrintView = { kind: "unknown", record, job: j, reason: j.error ?? "unknown" }; onView(v); return v; }
      if (j.state === "failed" || j.state === "cancelled") { drop(jobId); const v: PrintView = { kind: "failed", record, job: j, reason: j.error ?? j.state }; onView(v); return v; }
      onView({ kind: "working", state: j.state, record, job: j });
      // Spooled: Windows has it. Following it further only refines the state; the screen may stop here.
      if (j.state === "spooled" && Date.now() > end) return { kind: "working", state: "spooled", record, job: j };
    } else if (r.status === 404) {
      // The agent never stored this job: nothing was printed.
      drop(jobId);
      await report(record.id, "failed", "not_received_by_agent");
      const v: PrintView = { kind: "failed", record, reason: "not_received_by_agent" }; onView(v); return v;
    } else if (++misses >= 5) {
      const v: PrintView = { kind: "unknown", record, reason: "agent_unreachable" }; onView(v); return v;
    }
    if (Date.now() > end) return { kind: "working", state: last ?? "queued", record };
    await new Promise((res) => setTimeout(res, 500));
  }
}

export interface PrintRequest {
  invoiceId: string;
  role: Role;
  layout: PrintLayout;
  copy: boolean;
  title: string;
  /** Builds the document once the copy number is known (COPY n on a copy). */
  build: (copyNo: number, paper: AgentPaper) => PrintDoc;
  user: string;
  openDrawer?: boolean;
  /** Use this job id (a retry of a failed job that printed nothing). */
  jobId?: string;
}

/** Claims, renders, sends and follows one print. Never throws: every outcome is a view. */
export async function printDirect(req: PrintRequest, onView: (v: PrintView) => void): Promise<PrintView> {
  const m = await roleMapping(req.role);
  if (!m.ok) { const v: PrintView = { kind: "failed", reason: m.reason }; onView(v); return v; }
  const jobId = req.jobId ?? crypto.randomUUID();
  onView({ kind: "working", state: "claiming" });
  let record: PrintRecordDto;
  try {
    record = await api<PrintRecordDto>("POST", `/invoices/${req.invoiceId}/prints`, { jobId, channel: "agent", layout: req.layout, workstation: workstationName(), copy: req.copy });
  } catch (e) {
    const rec = e instanceof ApiFailure ? e.body.record as PrintRecordDto | undefined : undefined;
    if (e instanceof ApiFailure && e.code === "original_printed" && rec) { const v: PrintView = { kind: "original_exists", record: rec }; onView(v); return v; }
    const v: PrintView = { kind: "failed", reason: e instanceof ApiFailure ? e.code : "delta_unreachable" }; onView(v); return v;
  }
  const ref = documentRef(req.invoiceId, record.copyNo);
  keep({ jobId, invoiceId: req.invoiceId, recordId: record.id, documentRef: ref });
  onView({ kind: "working", state: "rendering", record });
  let pages: Raster[];
  try {
    await loadPrintFonts();
    pages = renderDoc(req.build(record.copyNo, m.mapping.paper), { widthMm: m.mapping.paper.widthMm, heightMm: m.mapping.paper.heightMm, dpi: m.mapping.paper.dpi });
  } catch {
    drop(jobId);
    await report(record.id, "failed", "render_failed");
    const v: PrintView = { kind: "failed", record, reason: "render_failed" }; onView(v); return v;
  }
  onView({ kind: "working", state: "sending", record });
  const body = { jobId, role: req.role, documentRef: ref, copy: record.copyNo > 0, title: req.title.slice(0, 120), pages, openDrawer: !!req.openDrawer, user: req.user };
  let sent: AgentReply<AgentJob> = await agentCall<AgentJob>("POST", "/print/jobs", body, savedToken(), 60_000);
  // No answer: the agent may have stored it. Look it up before sending again (same job id, so never twice).
  if (sent.status === null) { const again = await agentJob(jobId); sent = again.status === 404 ? await agentCall<AgentJob>("POST", "/print/jobs", body, savedToken(), 60_000) : again; }
  if (sent.status === null) { const v: PrintView = { kind: "unknown", record, reason: "agent_unreachable" }; onView(v); return v; }
  if (!sent.body) {
    // Refused before it was stored: nothing printed from this request.
    drop(jobId);
    const reason = sent.code ?? `http_${sent.status}`;
    const rec = await report(record.id, reason === "original_exists" ? "unknown" : "failed", reason);
    const v: PrintView = reason === "original_exists" ? { kind: "unknown", record: rec ?? record, reason } : { kind: "failed", record: rec ?? record, reason };
    onView(v); return v;
  }
  return follow(record, jobId, onView);
}

/** A failed print sent again with the same job id and copy number: safe, because the failed job printed nothing. */
export async function retryFailed(job: AgentJob, record: PrintRecordDto, onView: (v: PrintView) => void): Promise<PrintView> {
  const r = await agentCall<AgentJob>("POST", `/print/jobs/${job.jobId}/retry`, {});
  if (!r.body) { const v: PrintView = { kind: "failed", record, job, reason: r.code ?? "retry_refused" }; onView(v); return v; }
  keep({ jobId: job.jobId, invoiceId: record.invoiceId, recordId: record.id, documentRef: job.documentRef });
  return follow(record, job.jobId, onView);
}

/** The cashier saw an unknown (or still spooled) job printed. */
export async function confirmPrinted(record: PrintRecordDto, jobId: string, user: string): Promise<PrintRecordDto | null> {
  await agentCall("POST", `/print/jobs/${jobId}/confirm`, { user });
  return report(record.id, "confirmed");
}

/** A print through the browser's own print dialog (the fallback): its result is never known to Delta. */
export async function claimBrowserPrint(invoiceId: string, layout: PrintLayout, copy: boolean): Promise<PrintRecordDto | { originalExists: PrintRecordDto }> {
  try {
    return await api<PrintRecordDto>("POST", `/invoices/${invoiceId}/prints`, { jobId: crypto.randomUUID(), channel: "browser", layout, workstation: workstationName(), copy });
  } catch (e) {
    const rec = e instanceof ApiFailure ? e.body.record as PrintRecordDto | undefined : undefined;
    if (e instanceof ApiFailure && e.code === "original_printed" && rec) return { originalExists: rec };
    throw e;
  }
}

/** Looks again at prints this browser started and never heard the end of, and tells Delta what the agent knows. */
export async function settlePending(): Promise<void> {
  for (const p of pending()) {
    const r = await agentJob(p.jobId);
    if (r.status === null) return;
    if (r.status === 404) { drop(p.jobId); await report(p.recordId, "failed", "not_received_by_agent"); continue; }
    if (r.body && TERMINAL.includes(r.body.state)) { drop(p.jobId); await report(p.recordId, r.body.state as Exclude<AgentJobState, "sending">, r.body.error); }
  }
}

/** What the screen shows about a print that has no Delta print record (a test page, a shift report). */
export type PlainView = { kind: "working" } | { kind: "done"; printer: string | null } | { kind: "failed" | "unknown"; reason: string; jobId?: string };

/**
 * Prints a document that is not a sale (no original or copy to track): renders it for the role's paper, sends it to
 * the agent under `ref` and follows the job. The agent's job id is fresh each time, so a retry is a new print; that
 * is right for a report, which may be printed any number of times.
 */
export async function printUnrecorded(role: Role, ref: string, title: string, build: (paper: AgentPaper) => PrintDoc, user: string, onView: (v: PlainView) => void,
  /** The offline register's receipts (docs/offline-pos.md): the original under the sale's id, so it is printed once. */
  opts: { jobId?: string; copy?: boolean } = {}): Promise<PlainView> {
  const done = (v: PlainView) => { onView(v); return v; };
  const m = await roleMapping(role);
  if (!m.ok) return done({ kind: "failed", reason: m.reason });
  onView({ kind: "working" });
  let pages: Raster[];
  try {
    await loadPrintFonts();
    pages = renderDoc(build(m.mapping.paper), { widthMm: m.mapping.paper.widthMm, heightMm: m.mapping.paper.heightMm, dpi: m.mapping.paper.dpi });
  } catch { return done({ kind: "failed", reason: "render_failed" }); }
  const jobId = opts.jobId ?? crypto.randomUUID();
  const r = await agentCall<AgentJob>("POST", "/print/jobs", { jobId, role, documentRef: ref, copy: !!opts.copy, title: title.slice(0, 120), pages, user }, savedToken(), 60_000);
  if (r.status === null) return done({ kind: "unknown", reason: "agent_unreachable" });
  if (!r.body) return done({ kind: "failed", reason: r.code ?? `http_${r.status}` });
  for (let i = 0, misses = 0; i < 240; i++) {
    const j = await agentJob(jobId);
    if (j.body) {
      misses = 0;
      if (j.body.state === "done") return done({ kind: "done", printer: j.body.printer });
      if (j.body.state === "failed" || j.body.state === "cancelled") return done({ kind: "failed", reason: j.body.error ?? j.body.state, jobId });
      if (j.body.state === "unknown") return done({ kind: "unknown", reason: j.body.error ?? "unknown", jobId });
    } else if (++misses >= 5) return done({ kind: "unknown", reason: "agent_unreachable" });
    await new Promise((res) => setTimeout(res, 500));
  }
  return done({ kind: "unknown", reason: "spooled_no_answer" });
}
